import React from 'react';
import PropTypes from 'prop-types';
import { Tile, Typography } from '@one-thd/sui-atomic-components';
import { Add, ConfirmedFilled } from '@one-thd/sui-icons';
import {
  HAUL_AWAY_TEXT,
  INSTALL_TEXT,
  PROTECTION_TEXT
} from '../../constants';
import {
  getCheapestItem,
  getInstallationTextLabel,
  getHaulAwayTextLabel,
  getProtectionTextLabel
} from '../../utils';

const OPTIONS_MAP = {
  protection: {
    title: PROTECTION_TEXT,
    pageType: 'protection'
  },
  installation: {
    title: INSTALL_TEXT,
    pageType: 'services'
  },
  haulAway: {
    title: HAUL_AWAY_TEXT,
    pageType: 'haulAway'
  },
};

// Calculate default value based on type
const getDefaultValue = (type, availableItems) => {
  if (!availableItems || availableItems.length === 0) {
    return null;
  }

  if (type === 'protection') {
    return getCheapestItem(availableItems);
  }
  if (type === 'haulAway') {
    const cheapest = getCheapestItem(availableItems);
    return cheapest ? [cheapest] : availableItems;
  }

  return availableItems;
};

const getDisplayData = (type, hasUserSelected, displayValue, availableItems) => {
  if (type === 'protection') {
    const displayPrice = hasUserSelected
      ? displayValue?.price
      : getCheapestItem(availableItems)?.price || 0;
    return { displayPrice, displayLabel: getProtectionTextLabel(hasUserSelected, displayValue) };
  }
  if (type === 'installation') {
    const isHookupInstallation = hasUserSelected
      ? displayValue?.find(item => item?.configAttr === 'Hookup')
      : availableItems?.find(item => item?.configAttr === 'Hookup');
    const displayPrice = hasUserSelected
      ? displayValue?.find(item => item?.category === 'required')?.price || 0
      : availableItems?.find(item => item?.category === 'required')?.price || 0;
    return { isHookupInstallation, displayPrice, displayLabel: getInstallationTextLabel(hasUserSelected, isHookupInstallation) };
  }
  // haulAway
  const haulPrice = hasUserSelected
    ? displayValue?.find(item => item?.configAttr === '174602')?.price || 0
    : 0;
  const movePrice = hasUserSelected
    ? displayValue?.find(item => item?.configAttr === '853290')?.price || 0
    : 0;
  const displayPrice = hasUserSelected
    ? haulPrice + movePrice
    : getCheapestItem(availableItems)?.price || 0;
  return { displayPrice, displayLabel: getHaulAwayTextLabel(hasUserSelected, displayPrice, haulPrice, movePrice) };
};

export const DropDownButton = ({
  type,
  onClick,
  userSelection,
  availableItems,
  isBopisAndPickup
}) => {
  const { title, pageType } = OPTIONS_MAP[type];
  const hasUserSelected = !!userSelection;

  // Get display value: user selection or calculated default
  const displayValue = userSelection || getDefaultValue(type, availableItems);

  const { isHookupInstallation, displayPrice, displayLabel } = getDisplayData(
    type, hasUserSelected, displayValue, availableItems
  );

  const tileClassName = hasUserSelected
    ? 'sui-border-2 sui-border-solid sui-border-strongest sui-rounded-base sui-p-2'
    : 'sui-border-1 sui-border-solid sui-border-primary sui-rounded-base sui-p-2 hover:sui-border-2 hover:sui-border-solid hover:sui-border-accent';

  return (
    <Tile
      value={title}
      aria-label={title}
      onClick={isBopisAndPickup ? undefined : () => {
        setTimeout(() => onClick(pageType), 0);
      }}
      className={tileClassName}
      disabled={isBopisAndPickup}
    >
      <div className="sui-p-2 sui-w-full sui-flex sui-justify-between sui-items-center">
        <div className="sui-flex sui-flex-col sui-gap-1">
          <Typography variant="h4" color={isBopisAndPickup ? 'inactive' : undefined}>
            {title}
            {!isBopisAndPickup && isHookupInstallation && (
              <Typography variant="h4" component="span" weight="bold">
                <span className="sui-text-success" data-testid="free-installation-text"> FREE</span>
              </Typography>
            )}
          </Typography>
          {!isBopisAndPickup && (
            <Typography height="tight">
              {displayLabel}
              <Typography component="span" weight="bold"> ${displayPrice}</Typography>
            </Typography>
          )}
        </div>
        {!isBopisAndPickup && (
          hasUserSelected ? (
            <ConfirmedFilled size="regular" color="brand" />
          ) : (
            <Add size="small" />
          )
        )}
      </div>
    </Tile>
  );
};

DropDownButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['protection', 'installation', 'haulAway']).isRequired,
  userSelection: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.object)
  ]),
  availableItems: PropTypes.arrayOf(PropTypes.shape({})),
  isBopisAndPickup: PropTypes.bool
};

DropDownButton.defaultProps = {
  userSelection: null,
  availableItems: [],
  isBopisAndPickup: false
};

DropDownButton.displayName = 'DropDownButton';
